import CornerBg from "@/assets/img/illustrations/corner-2.png";
import Background from "@/components/atoms/Background";
import ErrorComponent from "@/components/atoms/ErrorComponent";
import Flex from "@/components/atoms/Flex";
import IconButton from "@/components/atoms/IconButton";
import Loader from "@/components/atoms/Loader";
import SoftBadge from "@/components/atoms/SoftBadge";
import UnknownBadge from "@/components/atoms/UnknownBadge";
import { formatInterlocutor } from "@/helpers/CompanyHelpers";
import { CardDetailsProps } from "@/interfaces/Card";
import CompanyCourse from "@/interfaces/CompanyCourse";
import { WithRequired } from "@/interfaces/Fetch";
import { useGetCompanyCourseQuery } from "@/services/companies/courses";
import {
  faBan,
  faCheckCircle,
  faClock,
  faEye,
  faFile,
  faFileSignature,
  faPenFancy,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { DateTime } from "luxon";
import { ElementRef, FC, useEffect, useRef } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import CardHeader from "../../card-headers/CardHeader";
import StoreCompanyCourseNoteModal from "../../modals/company/courses/StoreCompanyCourseNoteModal";

const CompanyCourseDetailsCard: FC<
  WithRequired<CardDetailsProps<CompanyCourse>, "secondId">
> = ({ id, secondId, card, showDetailsButton, callBack }) => {
  const StoreCompanyCourseNoteRef =
    useRef<ElementRef<typeof StoreCompanyCourseNoteModal>>(null);

  const { data, isLoading, isError, error } = useGetCompanyCourseQuery([
    id,
    secondId,
  ]);
  // Une formation n'a pas d'interlocuteur à elle : c'est celui de l'entreprise.
  const companyInterlocutor = formatInterlocutor(
    data?.company?.interlocutorFirstname,
    data?.company?.interlocutorLastname
  );

  useEffect(() => {
    data && callBack?.(data);
  }, [data]);

  const onTriggerModal = () =>
    StoreCompanyCourseNoteRef.current?.showOrHiddenModal([id, secondId]);

  if (isLoading) {
    return <Loader absolute />;
  } else if (isError) {
    return <ErrorComponent error={error} absolute returnButton />;
  }

  return (
    <div
      className={classNames({
        card: card,
      })}
    >
      <StoreCompanyCourseNoteModal ref={StoreCompanyCourseNoteRef} />
      {card && <CardHeader title="Informations de la formation" />}
      <div
        className={classNames("bg-light", {
          "card-body": card,
        })}
      >
        <Card className="border rounded-3 bg-white dark__bg-1000 shadow-none">
          <Background
            image={CornerBg}
            className="bg-card d-none d-sm-block d-xl-none"
          />
          <Card.Body className="row g-0 flex-column flex-sm-row flex-xl-column z-index-1 align-items-center">
            <Col sm={8} md={6} lg={4} xl={12} className="ps-sm-1 ps-xl-0">
              <Flex alignItems="xl-center" direction="column">
                <p className="fw-semi-bold mb-0 text-800">
                  {data?.course?.name ?? "Formation inconnue"}
                </p>
                <Flex>
                  <SoftBadge
                    bg={data?.status?.backgroundColor ?? "danger"}
                    textColor={data?.status?.textColor}
                    className="my-2 text-xl-center"
                  >
                    {data?.status?.name ?? "Inconnu"}
                  </SoftBadge>
                  {data?.organisation && (
                    <SoftBadge
                      bg={data?.organisation?.backgroundColor ?? "danger"}
                      textColor={data?.organisation?.textColor}
                      className="my-2 ms-2 text-xl-center"
                    >
                      {data?.organisation?.name}
                    </SoftBadge>
                  )}
                  {data?.company.activitySector?.opco && (
                    <SoftBadge
                      bg={data?.company.activitySector?.opco?.backgroundColor ?? "danger"}
                      textColor={data?.company.activitySector?.opco?.textColor}
                      className="my-2 ms-2 text-xl-center"
                    >
                      {data?.company.activitySector?.opco?.name}
                    </SoftBadge>
                  )}
                </Flex>
              </Flex>
              <Flex justifyContent="center" className="gap-2">
                {showDetailsButton ? (
                  <IconButton
                    variant="primary"
                    icon={faEye}
                    iconAlign="left"
                    href={`/companies/${data!.id}`}
                    transform="shrink-3 down-1"
                    size="sm"
                    className="flex-grow-1 px-2 text-nowrap"
                  >
                    <span className="fs--2">Détails</span>
                  </IconButton>
                ) : (
                  <IconButton
                    variant="primary"
                    icon={faPlus}
                    iconAlign="left"
                    onClick={onTriggerModal}
                    transform="shrink-3 down-1"
                    size="sm"
                    className="w-50 px-2 text-nowrap"
                  >
                    <span className="fs--2">Note</span>
                  </IconButton>
                )}
              </Flex>
            </Col>
          </Card.Body>
        </Card>
        {data?.documentSigning && (
          <div
            className={classNames(
              "border rounded-3 p-3 mt-3 mx-0",
              {
                "border-success bg-soft-success": data.documentSigning.status === "completed",
                "border-warning bg-soft-warning": ["pending", "viewed", "started"].includes(data.documentSigning.status),
                "border-secondary bg-light": data.documentSigning.status === "draft",
                "border-danger bg-soft-danger": data.documentSigning.status === "failed" || data.documentSigning.status === "declined",
              }
            )}
          >
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center">
                <FontAwesomeIcon
                  icon={
                    data.documentSigning.status === "completed"
                      ? faCheckCircle
                      : data.documentSigning.status === "viewed"
                      ? faEye
                      : data.documentSigning.status === "started"
                      ? faPenFancy
                      : data.documentSigning.status === "declined"
                      ? faBan
                      : data.documentSigning.status === "failed"
                      ? faFile
                      : data.documentSigning.status === "pending"
                      ? faClock
                      : faFileSignature
                  }
                  className={classNames("me-2 fs-1", {
                    "text-success": data.documentSigning.status === "completed",
                    "text-warning": ["pending", "viewed", "started"].includes(data.documentSigning.status),
                    "text-secondary": data.documentSigning.status === "draft",
                    "text-danger": data.documentSigning.status === "failed" || data.documentSigning.status === "declined",
                  })}
                />
                <div>
                  <p className="fw-semi-bold mb-0">Convention</p>
                  <small className={classNames("fw-medium", {
                    "text-success": data.documentSigning.status === "completed",
                    "text-warning": ["pending", "viewed", "started"].includes(data.documentSigning.status),
                    "text-secondary": data.documentSigning.status === "draft",
                    "text-danger": data.documentSigning.status === "failed" || data.documentSigning.status === "declined",
                  })}>
                    {data.documentSigning.status === "completed" && "Signée"}
                    {data.documentSigning.status === "pending" && "Envoyée"}
                    {data.documentSigning.status === "viewed" && "Consultée"}
                    {data.documentSigning.status === "started" && "En cours de signature"}
                    {data.documentSigning.status === "declined" && "Refusée"}
                    {data.documentSigning.status === "draft" && "Brouillon"}
                    {data.documentSigning.status === "failed" && "Erreur"}
                  </small>
                </div>
              </div>
              {data.documentSigning.file?.link && (
                <IconButton
                  variant={data.documentSigning.status === "completed" ? "outline-success" : "outline-secondary"}
                  size="sm"
                  icon={faEye}
                  onClick={() => window.open(data.documentSigning!.file.link, "_blank")}
                >
                  <span className="ms-1 fs--2">Voir</span>
                </IconButton>
              )}
            </div>
          </div>
        )}

        <Row className="border rounded-3 p-x1 mt-3 bg-white dark__bg-1000 mx-0 g-0">
          <Col md={6} xl={12} className="pe-md-4 pe-xl-0">
            <div className="mb-2">
              <Form.Label>#</Form.Label>
              <p className="mb-0 text-700 fs--1">{data?.id}</p>
            </div>
            <div className="mb-2">
              <Form.Label>Organisation</Form.Label>
              <br />
              {data?.organisation ? (
                <SoftBadge
                  bg={data?.organisation?.backgroundColor ?? "danger"}
                  textColor={data?.organisation?.textColor}
                >
                  {data?.organisation?.name}
                </SoftBadge>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Nom de la formation</Form.Label>
              <p className="mb-0 text-700 fs--1">
                {data?.course?.name ?? "Formation inconnnue"}
              </p>
            </div>
            <div className="mb-2">
              <Form.Label>Description de la formation</Form.Label>
              <br />
              {data?.course?.description ? (
                <p className="mb-0 text-700 fs--1">
                  {data?.course.description}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Date de signature</Form.Label>
              <br />
              {data?.signatureDate ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.signatureDate).toFormat("dd-MM-yyyy")}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Numéro de dossier final</Form.Label>
              <br />
              {data?.finalFolderNumber ? (
                <span className="badge border link-secondary text-decoration-none me-1">
                  {data.finalFolderNumber}
                </span>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Interlocuteur</Form.Label>
              <br />
              {companyInterlocutor ? (
                <p className="mb-0 text-700 fs--1">{companyInterlocutor}</p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Adresse</Form.Label>
              <br />
              {data?.address ? (
                <p className="mb-0 text-700 fs--1">{data.address}</p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Date de relance</Form.Label>
              <br />
              {data?.followUpDate ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.followUpDate).toFormat("dd-MM-yyyy")}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Date de début</Form.Label>
              <br />
              {data?.startAt ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.startAt).toFormat("dd-MM-yyyy HH:mm")}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Date de fin</Form.Label>
              <br />
              {data?.endAt ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.endAt).toFormat("dd-MM-yyyy HH:mm")}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Nombre d'apprenants prévu</Form.Label>
              <br />
              {data?.peopleCount ? (
                <p className="mb-0 text-700 fs--1">{data.peopleCount}</p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Nombre d'heures prévu</Form.Label>
              <br />
              {data?.hoursNumber ? (
                <p className="mb-0 text-700 fs--1">{data.hoursNumber}</p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Montant demandé</Form.Label>
              <br />
              {data?.askingPrice ? (
                <span className="badge border link-secondary text-decoration-none me-1">
                  {data.askingPrice} €
                </span>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Date d'envoi</Form.Label>
              <br />
              {data?.sendingDate ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.sendingDate).toFormat("dd-MM-yyyy")}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Date de début de session</Form.Label>
              <br />
              {data?.sessionStartDate ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.sessionStartDate).toFormat(
                    "dd-MM-yyyy"
                  )}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Date de fin de session</Form.Label>
              <br />
              {data?.sessionEndDate ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.sessionEndDate).toFormat("dd-MM-yyyy")}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Date d'accord</Form.Label>
              <br />
              {data?.agreementDate ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.agreementDate).toFormat("dd-MM-yyyy")}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Montant accordé</Form.Label>
              <br />
              {data?.agreementAmount ? (
                <span className="badge border link-secondary text-decoration-none me-1">
                  {data.agreementAmount} €
                </span>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Montant de la facture</Form.Label>
              <br />
              {data?.invoiceAmount ? (
                <span className="badge border link-secondary text-decoration-none me-1">
                  {data.invoiceAmount}
                </span>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Numéro de facture</Form.Label>
              <br />
              {data?.invoiceNumber ? (
                <span className="badge border link-secondary text-decoration-none me-1">
                  {data.invoiceNumber}
                </span>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Date de la facture</Form.Label>
              <br />
              {data?.invoiceDate ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.invoiceDate).toFormat("dd-MM-yyyy")}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Date de paiement</Form.Label>
              <br />
              {data?.paiementDate ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.paiementDate).toFormat("dd-MM-yyyy")}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
            <div className="mb-2">
              <Form.Label>Date admin</Form.Label>
              <br />
              {data?.adminDate ? (
                <p className="mb-0 text-700 fs--1">
                  {DateTime.fromISO(data.adminDate).toFormat("dd-MM-yyyy")}
                </p>
              ) : (
                <UnknownBadge />
              )}
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default CompanyCourseDetailsCard;
